import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  QUEBRA_POR_LIGA_PLATAFORMA,
  QUEBRA_POR_TORNEIO_PLATAFORMA,
  QUEBRA_POR_CATEGORIA_PLATAFORMA,
  QuebraPorLigaPlataformaData,
  QuebraPorTorneioPlataformaData,
  QuebraPorTorneioPlataformaVars,
  QuebraPorCategoriaPlataformaData,
  QuebraPorCategoriaPlataformaVars,
  FinanceiroFiltroVars,
} from '../../../../services/financeiroService';
import { buscarLigasPorNome } from '../../../../services/ligaService';
import { FiltroAutocomplete } from '../../../../components/financeiro';
import type { FiltroAutocompleteOption } from '../../../../components/financeiro';
import { AppBreadcrumb } from '../../../../components/navigation/AppBreadcrumb';
import type { Crumb } from '../../../../components/navigation/AppBreadcrumb';
import { formatCurrency } from '../../../../utils/money';
import type { QuebraFinanceira, PeriodoFinanceiro } from '../../../../types/financeiro';

// Drill-down liga → torneio → categoria, mesmo idioma de useQuery com variáveis que mudam de
// pages/liga/financeiro/RecebimentosLigaTab.tsx:90-105 e formato de Crumb de
// components/navigation/AppBreadcrumb.tsx:23-30. Estado de drill-down é inteiramente local a
// este componente — nunca sobe para ?tab= do hub.
//
// Cada nível é buscado no máximo UMA VEZ por sessão da aba: o resultado de cada nível já
// visitado é guardado em cache local (não é refetch por "cache" do Apollo — `fetchPolicy` das 3
// queries continua 'no-cache', como as demais desta fase). Sem isso, voltar pelo breadcrumb para
// um nível já visitado disparia uma 2ª consulta de rede idêntica.

export interface PorLigaTabProps {
  periodo: PeriodoFinanceiro;
}

type Nivel = 'liga' | 'torneio' | 'categoria';

interface Selecao {
  chaveId: string;
  chaveNome: string;
}

interface CacheChave {
  key: string;
  linhas: QuebraFinanceira[];
}

const PorLigaTab: React.FC<PorLigaTabProps> = ({ periodo }) => {
  const [nivel, setNivel] = useState<Nivel>('liga');
  const [ligaSelecionada, setLigaSelecionada] = useState<Selecao | null>(null);
  const [torneioSelecionado, setTorneioSelecionado] = useState<Selecao | null>(null);

  const fetchLigas = async (q: string): Promise<FiltroAutocompleteOption[]> => {
    const conexao = await buscarLigasPorNome(q);
    return (conexao.items || []).map((liga) => ({ id: liga.id, label: liga.nome }));
  };

  // O filtro de liga é o MESMO estado do drill-down, não um segundo estado paralelo — selecionar
  // aqui é um atalho para o nível torneio daquela liga, e descer clicando numa linha reflete de
  // volta no campo. Antes de 2026-08-18 eram dois estados (o filtro no topo do hub e a seleção do
  // drill-down), sincronizados por um useEffect que só reagia quando o id mudava para OUTRO id:
  // limpar o filtro não voltava ao nível liga — a tela seguia nos torneios da liga anterior com o
  // campo já vazio.
  const selecionarLigaPeloFiltro = (opcao: FiltroAutocompleteOption | null) => {
    setTorneioSelecionado(null);
    if (!opcao) {
      setLigaSelecionada(null);
      setNivel('liga');
      return;
    }
    setLigaSelecionada({ chaveId: opcao.id, chaveNome: opcao.label });
    setNivel('torneio');
  };

  const [ligaCache, setLigaCache] = useState<QuebraFinanceira[] | null>(null);
  const [torneioCache, setTorneioCache] = useState<CacheChave | null>(null);
  const [categoriaCache, setCategoriaCache] = useState<CacheChave | null>(null);

  const torneioLigaId = ligaSelecionada?.chaveId ?? '';
  const torneioCacheHit = torneioCache?.key === torneioLigaId;

  const categoriaTorneioId = torneioSelecionado?.chaveId ?? '';
  const categoriaCacheHit = categoriaCache?.key === categoriaTorneioId;

  const ligaQuery = useQuery<QuebraPorLigaPlataformaData, FinanceiroFiltroVars>(
    QUEBRA_POR_LIGA_PLATAFORMA,
    {
      variables: { filtro: periodo },
      skip: nivel !== 'liga' || ligaCache !== null,
      fetchPolicy: 'no-cache',
      onCompleted: (data) => setLigaCache(data.quebraPorLigaPlataforma ?? []),
    },
  );

  const torneioQuery = useQuery<QuebraPorTorneioPlataformaData, QuebraPorTorneioPlataformaVars>(
    QUEBRA_POR_TORNEIO_PLATAFORMA,
    {
      variables: { ligaId: torneioLigaId, filtro: periodo },
      skip: nivel !== 'torneio' || !ligaSelecionada || torneioCacheHit,
      fetchPolicy: 'no-cache',
      onCompleted: (data) =>
        setTorneioCache({ key: torneioLigaId, linhas: data.quebraPorTorneioPlataforma ?? [] }),
    },
  );

  const categoriaQuery = useQuery<QuebraPorCategoriaPlataformaData, QuebraPorCategoriaPlataformaVars>(
    QUEBRA_POR_CATEGORIA_PLATAFORMA,
    {
      variables: { torneioId: categoriaTorneioId, filtro: periodo },
      skip: nivel !== 'categoria' || !torneioSelecionado || categoriaCacheHit,
      fetchPolicy: 'no-cache',
      onCompleted: (data) =>
        setCategoriaCache({ key: categoriaTorneioId, linhas: data.quebraPorCategoriaPlataforma ?? [] }),
    },
  );

  let linhas: QuebraFinanceira[];
  let loading: boolean;
  let error: Error | undefined;
  let retry: () => void;
  let colunaLabel: string;

  if (nivel === 'liga') {
    linhas = ligaCache ?? (ligaQuery.data?.quebraPorLigaPlataforma ?? []);
    loading = ligaCache === null && ligaQuery.loading;
    error = ligaCache === null ? ligaQuery.error : undefined;
    retry = () => ligaQuery.refetch();
    colunaLabel = 'Liga';
  } else if (nivel === 'torneio') {
    linhas = torneioCacheHit
      ? (torneioCache as CacheChave).linhas
      : torneioQuery.data?.quebraPorTorneioPlataforma ?? [];
    loading = !torneioCacheHit && torneioQuery.loading;
    error = !torneioCacheHit ? torneioQuery.error : undefined;
    retry = () => torneioQuery.refetch();
    colunaLabel = 'Torneio';
  } else {
    linhas = categoriaCacheHit
      ? (categoriaCache as CacheChave).linhas
      : categoriaQuery.data?.quebraPorCategoriaPlataforma ?? [];
    loading = !categoriaCacheHit && categoriaQuery.loading;
    error = !categoriaCacheHit ? categoriaQuery.error : undefined;
    retry = () => categoriaQuery.refetch();
    colunaLabel = 'Categoria';
  }

  const irParaLigas = () => {
    setNivel('liga');
    setLigaSelecionada(null);
    setTorneioSelecionado(null);
  };

  const irParaTorneios = () => {
    setNivel('torneio');
    setTorneioSelecionado(null);
  };

  const avancar = (linha: QuebraFinanceira) => {
    if (!linha.chaveId || !linha.chaveNome) return;
    if (nivel === 'liga') {
      setLigaSelecionada({ chaveId: linha.chaveId, chaveNome: linha.chaveNome });
      setNivel('torneio');
    } else if (nivel === 'torneio') {
      setTorneioSelecionado({ chaveId: linha.chaveId, chaveNome: linha.chaveNome });
      setNivel('categoria');
    }
  };

  const crumbs: Crumb[] = [{ label: 'Ligas', onClick: nivel !== 'liga' ? irParaLigas : undefined }];
  if (ligaSelecionada) {
    crumbs.push({
      label: ligaSelecionada.chaveNome,
      onClick: nivel === 'categoria' ? irParaTorneios : undefined,
    });
  }
  if (torneioSelecionado && nivel === 'categoria') {
    crumbs.push({ label: torneioSelecionado.chaveNome });
  }

  return (
    <div>
      <div className="mb-3" style={{ maxWidth: 320 }} data-testid="porliga-filtro-liga">
        <FiltroAutocomplete
          label="Liga"
          value={
            ligaSelecionada ? { id: ligaSelecionada.chaveId, label: ligaSelecionada.chaveNome } : null
          }
          onChange={selecionarLigaPeloFiltro}
          fetchOptions={fetchLigas}
        />
      </div>

      <div className="mb-3">
        <AppBreadcrumb items={crumbs} />
      </div>

      {error ? (
        <div className="text-center py-5" data-testid="porliga-erro">
          <p className="text-danger mb-3">Não foi possível carregar os dados. Tente novamente.</p>
          <button type="button" className="btn btn-primary" onClick={retry}>
            Tentar novamente
          </button>
        </div>
      ) : loading ? (
        <div className="text-center py-4">Carregando...</div>
      ) : linhas.length === 0 ? (
        <div className="text-center py-5" data-testid="porliga-vazio">
          <p className="mb-3">Nenhum lançamento neste nível para o período selecionado.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{colunaLabel}</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => (
              <tr
                key={linha.chaveId ?? linha.chaveNome ?? 'sem-chave'}
                style={{ cursor: nivel === 'categoria' ? 'default' : 'pointer' }}
                onClick={() => avancar(linha)}
              >
                <td>{linha.chaveNome ?? 'Não informado'}</td>
                <td>{formatCurrency(linha.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PorLigaTab;
